/**
 * Preparing a 3MF for three's loader: what the loader gets wrong, corrected before it sees
 * the file.
 *
 * Two things go wrong, and both are silent — the model simply comes out looking incorrect,
 * with nothing to say why.
 *
 * **Multi-property faces vanish.** `3MFLoader` knows three kinds of property group — base
 * materials, colour groups and texture groups — and drops every triangle belonging to any
 * other. A `<multiproperties>` stacks several groups on the same triangles, so a file using
 * one has a hole where those faces should be. The 3MF Consortium's own samples use it.
 *
 * **A stacked texture is drawn as though it were opaque.** The loader builds a material
 * with `transparent` left off, so a texture's alpha channel is discarded and whatever bytes
 * happen to sit under the transparent pixels are drawn instead — which for a picture with a
 * cut-out background is arbitrary blocks of colour. The layers are stacked precisely
 * *because* the upper ones have holes in them, so this affects exactly the files the first
 * problem does.
 *
 * Both are fixed here rather than in the loader: the layers are flattened into a single
 * opaque image, and the triangles are redirected at the texture group holding it. What the
 * loader then does is the ordinary case it already handles well.
 *
 * The unit is read while the document is open, since three reads it and never applies it.
 *
 * This is the decompressor-and-XML-parser that `domain/` was kept clear of, which is why it
 * lives here beside the loaders. Only the decisions are pure, and those are in
 * `domain/threeMf.ts`.
 */

import { unzipSync, zipSync } from 'three/addons/libs/fflate.module.js';

import {
  chooseLayer,
  imageHasAlpha,
  millimetresPerUnit,
  opacityOf,
  type ChosenLayer,
  type PropertyGroup,
} from '../domain/threeMf.js';
import { logger } from '../log.js';

const log = logger('models');

/** Which element names stand for a property group the loader can draw from. */
const GROUPS = new Map<string, PropertyGroup>([
  ['texture2dgroup', 'texture'],
  ['colorgroup', 'colour'],
  ['basematerials', 'material'],
]);

/** What a part with no colour of its own is drawn as, matching the loader's own default. */
const NO_BASE_COLOUR = '#ffffff';

export interface ThreeMfSource {
  /** The archive to hand the loader, rewritten only if it needed it. */
  readonly data: ArrayBuffer;
  /** Millimetres per unit of the file's coordinates, from the unit it declares. */
  readonly millimetresPerUnit: number;
  /**
   * What the file says about being see-through, which the loader would otherwise lose.
   *
   * Read here because it is a question about the archive rather than about the scene: an
   * image's alpha channel is stated in its header and gone by the time it is a texture,
   * and a colour group's alpha is dropped while the file is being parsed.
   */
  readonly translucency: Translucency;
}

export interface Translucency {
  /** True where some texture in the file has an alpha channel. */
  readonly inTextures: boolean;
  /**
   * The opacity a colour group declares, where every colour it uses declares the same one.
   *
   * A single figure because that is what can be honoured: three's loader builds one
   * material per group with a three-component colour attribute, so an opacity varying from
   * vertex to vertex has nowhere to live. `undefined` where nothing is translucent, or
   * where the group's colours disagree and no single figure would be true.
   */
  readonly ofColourGroups: number | undefined;
}

const OPAQUE: Translucency = { inTextures: false, ofColourGroups: undefined };

/**
 * Read what the loader needs to know, and correct what it gets wrong.
 *
 * Never throws. A file this cannot make sense of is handed back as it came, so the worst
 * outcome is the model we would have had anyway rather than one that will not open.
 */
export async function read3mf(data: ArrayBuffer): Promise<ThreeMfSource> {
  try {
    return await prepare(data);
  } catch (error) {
    log.warn('A 3MF could not be prepared; using it as it came.', error);
    return { data, millimetresPerUnit: 1, translucency: OPAQUE };
  }
}

async function prepare(data: ArrayBuffer): Promise<ThreeMfSource> {
  const entries = unzipSync(new Uint8Array(data));

  const modelName = Object.keys(entries).find((name) =>
    name.toLowerCase().endsWith('.model'),
  );
  const modelBytes = modelName === undefined ? undefined : entries[modelName];
  if (modelName === undefined || !modelBytes) {
    return { data, millimetresPerUnit: 1, translucency: OPAQUE };
  }

  const document = new DOMParser().parseFromString(
    new TextDecoder().decode(modelBytes),
    'application/xml',
  );
  if (document.getElementsByTagName('parsererror').length > 0) {
    return { data, millimetresPerUnit: 1, translucency: OPAQUE };
  }

  const scale = millimetresPerUnit(document.documentElement.getAttribute('unit'));
  if (scale !== 1) {
    log.info(
      `This 3MF declares its coordinates in units of ${scale.toString()} mm; scaling to suit.`,
    );
  }

  const translucency = translucencyOf(document, entries);

  const groups = multiPropertyGroups(document);
  // The common case, and the one worth being quick about: nothing needs changing, so the
  // archive is not rebuilt at all.
  if (groups.length === 0) return { data, millimetresPerUnit: scale, translucency };

  await Promise.all(groups.map((group) => flatten(group, document, entries)));

  const redirected = redirect(document, groups);
  if (redirected === 0) return { data, millimetresPerUnit: scale, translucency };

  log.info(
    `Redirected ${redirected.toString()} multi-property face${redirected === 1 ? '' : 's'} in a 3MF onto a layer that can be drawn.`,
  );

  entries[modelName] = new TextEncoder().encode(
    new XMLSerializer().serializeToString(document),
  );

  // Stored rather than deflated. This archive is about to be unzipped again a few lines
  // further on and then thrown away, so compressing it would be work done to undo itself.
  return {
    data: zipSync(entries, { level: 0 }).buffer,
    millimetresPerUnit: scale,
    translucency,
  };
}

interface Layer {
  readonly pid: string;
  readonly group: PropertyGroup;
  readonly element: Element;
}

interface MultiPropertyGroup {
  readonly id: string;
  readonly layers: readonly Layer[];
  readonly rows: readonly (readonly number[])[];
  readonly chosen: ChosenLayer;
}

/**
 * Every multi-property group in the document, resolved to the layer to draw instead.
 *
 * Read namespace-agnostically. The materials extension has carried more than one namespace
 * over its life and files use different prefixes for it, so matching on the local name is
 * the only reading that works across all of them.
 */
function multiPropertyGroups(document: Document): MultiPropertyGroup[] {
  const known = new Map<string, Layer>();
  for (const [name, group] of GROUPS) {
    for (const element of byLocalName(document, name)) {
      const pid = element.getAttribute('id');
      if (pid !== null) known.set(pid, { pid, group, element });
    }
  }

  const found: MultiPropertyGroup[] = [];

  for (const node of byLocalName(document, 'multiproperties')) {
    const id = node.getAttribute('id');
    const pids = numbersOrNames(node.getAttribute('pids'));
    if (id === null || pids.length === 0) continue;

    const rows = byLocalName(node, 'multi').map((row) =>
      numbersOrNames(row.getAttribute('pindices')).map((value) => {
        const index = Number.parseInt(value, 10);
        return Number.isInteger(index) ? index : 0;
      }),
    );

    const chosen = chooseLayer({ pids, rows }, (pid) => known.get(pid)?.group);
    if (!chosen) continue;

    const layers = pids
      .map((pid) => known.get(pid))
      .filter((layer): layer is Layer => layer !== undefined);

    found.push({ id, layers, rows, chosen });
  }

  return found;
}

/**
 * Draw a group's layers onto one another, leaving the result in the chosen texture.
 *
 * The image is replaced in place rather than added alongside. A new part would need a
 * relationship and a content type declaring it, and the texture being overwritten is one
 * this group alone draws from — it is about to become the only thing rendered from these
 * triangles either way.
 *
 * Does nothing where the chosen layer is not a texture: a colour group needs no flattening,
 * being opaque already.
 */
async function flatten(
  group: MultiPropertyGroup,
  document: Document,
  entries: Record<string, Uint8Array>,
): Promise<void> {
  const chosen = group.layers.find((layer) => layer.pid === group.chosen.pid);
  if (chosen?.group !== 'texture') return;

  // Only the layers sharing the chosen one's coordinates can be drawn onto the same image.
  // A layer mapped differently would need its own, and there is one image to give.
  const coordinates = textureCoordinates(chosen.element);
  const drawn = group.layers.filter(
    (layer) =>
      layer.group === 'texture' &&
      textureCoordinates(layer.element) === coordinates &&
      group.layers.indexOf(layer) >= group.layers.indexOf(chosen),
  );

  const images = await Promise.all(
    drawn.map((layer) => decode(imagePath(layer.element, document), entries)),
  );
  const readable = images.filter((image): image is ImageBitmap => image !== undefined);
  if (readable.length === 0) return;

  const composited = await paint(baseColourOf(group), readable);
  if (!composited) return;

  const path = imagePath(chosen.element, document);
  if (path === undefined) return;

  entries[path.replace(/^\//, '')] = composited;
  for (const image of readable) image.close();
}

/**
 * What the layers beneath the chosen one amount to, as a colour to paint behind it.
 *
 * The stacking exists so that upper layers can have holes in them, and what shows through
 * a hole is whatever is underneath. Only a layer that resolves to one colour across every
 * row can be reduced to a fill; anything varying per triangle would need painting per
 * triangle, which is a different exercise from flattening an image.
 */
function baseColourOf(group: MultiPropertyGroup): string {
  const chosenAt = group.layers.findIndex((layer) => layer.pid === group.chosen.pid);

  for (let at = 0; at < chosenAt; at += 1) {
    const layer = group.layers[at];
    if (!layer || layer.group === 'texture') continue;

    const indices = new Set(group.rows.map((row) => row[at] ?? 0));
    if (indices.size !== 1) continue;

    const colour = colourAt(layer.element, [...indices][0] ?? 0);
    if (colour !== undefined) return colour;
  }

  return NO_BASE_COLOUR;
}

/** The nth colour a colour group or base-material group declares. */
function colourAt(element: Element, index: number): string | undefined {
  const entries = [...byLocalName(element, 'color'), ...byLocalName(element, 'base')];
  const chosen = entries[index];
  if (!chosen) return undefined;

  const value = chosen.getAttribute('color') ?? chosen.getAttribute('displaycolor');
  // The format writes eight digits where a colour carries alpha. The fill behind a picture
  // is opaque by definition, so the last pair is dropped rather than honoured.
  return value === null ? undefined : value.slice(0, 7);
}

/** Paint a background and then each image over it, and give back the result as a PNG. */
async function paint(
  background: string,
  images: readonly ImageBitmap[],
): Promise<Uint8Array | undefined> {
  const first = images[0];
  if (!first) return undefined;

  const canvas = document.createElement('canvas');
  canvas.width = first.width;
  canvas.height = first.height;

  const context = canvas.getContext('2d');
  if (!context) return undefined;

  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  for (const image of images) {
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });
  if (!blob) return undefined;

  return new Uint8Array(await blob.arrayBuffer());
}

/** Decode a texture part out of the archive, or undefined if it cannot be read. */
async function decode(
  path: string | undefined,
  entries: Record<string, Uint8Array>,
): Promise<ImageBitmap | undefined> {
  if (path === undefined) return undefined;

  const bytes = entries[path.replace(/^\//, '')];
  if (!bytes) return undefined;

  try {
    // Copied into a buffer of its own: the entry is a view onto the archive, and a blob
    // built from a view keeps the whole archive alive behind it.
    return await createImageBitmap(new Blob([bytes.slice()]));
  } catch {
    return undefined;
  }
}

/** Where a texture group's image lives, by way of the texture it names. */
function imagePath(group: Element, document: Document): string | undefined {
  const texid = group.getAttribute('texid');
  if (texid === null) return undefined;

  for (const texture of byLocalName(document, 'texture2d')) {
    if (texture.getAttribute('id') === texid) {
      return texture.getAttribute('path') ?? undefined;
    }
  }

  return undefined;
}

/** A texture group's coordinates, as one string, so two groups can be compared for sameness. */
function textureCoordinates(group: Element): string {
  return byLocalName(group, 'tex2coord')
    .map((entry) => `${entry.getAttribute('u') ?? ''},${entry.getAttribute('v') ?? ''}`)
    .join(' ');
}

/** Point every triangle and object at its chosen layer. Returns how many were moved. */
function redirect(document: Document, groups: readonly MultiPropertyGroup[]): number {
  const chosen = new Map(groups.map((group) => [group.id, group.chosen]));
  let moved = 0;

  for (const triangle of byLocalName(document, 'triangle')) {
    const layer = chosen.get(triangle.getAttribute('pid') ?? '');
    if (!layer) continue;

    // `p1` stands in for any corner the file leaves out, which is what the format says an
    // absent `p2` or `p3` means.
    const first = indexAttribute(triangle, 'p1');
    for (const corner of ['p1', 'p2', 'p3'] as const) {
      if (corner !== 'p1' && !triangle.hasAttribute(corner)) continue;
      triangle.setAttribute(
        corner,
        layer.indexFor(indexAttribute(triangle, corner) ?? first ?? 0).toString(),
      );
    }

    triangle.setAttribute('pid', layer.pid);
    moved += 1;
  }

  // An object may name a default property for the triangles that give none of their own.
  for (const object of byLocalName(document, 'object')) {
    const layer = chosen.get(object.getAttribute('pid') ?? '');
    if (!layer) continue;

    object.setAttribute(
      'pindex',
      layer.indexFor(indexAttribute(object, 'pindex') ?? 0).toString(),
    );
    object.setAttribute('pid', layer.pid);
  }

  return moved;
}

function indexAttribute(node: Element, name: string): number | undefined {
  const raw = node.getAttribute(name);
  if (raw === null) return undefined;

  const value = Number.parseInt(raw, 10);
  return Number.isInteger(value) ? value : undefined;
}

function numbersOrNames(value: string | null): string[] {
  return (value ?? '').trim().split(/\s+/).filter(Boolean);
}

/**
 * Elements with this local name, whatever namespace prefix they carry.
 *
 * `getElementsByTagNameNS` with a wildcard namespace is the one call that ignores the
 * prefix; `getElementsByTagName` matches the qualified name and would miss `m:colorgroup`
 * in a file that spells the prefix differently.
 */
function byLocalName(root: Document | Element, name: string): Element[] {
  return [...root.getElementsByTagNameNS('*', name)];
}

/**
 * What the file says about being see-through.
 *
 * Both answers are lost by the time the loader has finished: an image's alpha channel is
 * declared in its header and invisible once decoded, and a colour group's alpha is dropped
 * where its colours are parsed. Read here, while the archive is still open.
 *
 * A base material's opacity needs no help — the loader does read that one, and only
 * forgets to mark the material transparent, which `sceneFile` puts right from the number
 * already on it.
 */
function translucencyOf(
  document: Document,
  entries: Record<string, Uint8Array>,
): Translucency {
  const inTextures = byLocalName(document, 'texture2d').some((texture) => {
    const path = texture.getAttribute('path')?.replace(/^\//, '');
    const bytes = path === undefined ? undefined : entries[path];
    return bytes !== undefined && imageHasAlpha(bytes);
  });

  const declared = new Set(
    byLocalName(document, 'colorgroup').flatMap((group) =>
      byLocalName(group, 'color').map((colour) =>
        opacityOf(colour.getAttribute('color')),
      ),
    ),
  );

  // One figure or none. Several different ones would each be true of some vertices and
  // false of the rest, and a material carries only one — see `Translucency`.
  const only = declared.size === 1 ? [...declared][0] : undefined;
  const ofColourGroups = only !== undefined && only < 1 ? only : undefined;

  if (inTextures || ofColourGroups !== undefined) {
    log.info('This 3MF describes surfaces that let light through.');
  }

  return { inTextures, ofColourGroups };
}
